<script setup lang="ts">
    import WidgetCalendar from '@/components/WidgetCalendar.ce.vue';
    import WidgetHours from '@/components/WidgetHours.ce.vue';
    import { type FilterActionTyping } from '@/typings/widget';
    import Icon from '@shared/components/ui/icon/icon.vue';
    import { ConstantsDateTime } from '@shared/constants';
    import { DateTime } from '@shared/helpers';
    import WidgetRangeInput from '../WidgetRangeInput.ce.vue';

    withDefaults(defineProps<{
        maxGuestAmount?: number
        minGuestAmount?: number
        guestAmountStep?: number
        styling?: 'primary' | 'secondary'
        hideDate?: boolean
        hideTime?: boolean
        hideGuests?: boolean
        detached?: boolean
        detachedPosition?: 'left' | 'right' | 'center'
    }>(), {
        styling: 'primary',
        maxGuestAmount: 1000,
        minGuestAmount: 0,
        guestAmountStep: 1,
        detachedPosition: 'left',
    });

    // Composables
    const { t } = useI18n();
    const { isMobile } = useAppBreakpoints();

    // Stores
    const { guestAmountFilter, startDateFilter, startTimeFilter } = storeToRefs(useWidgetFilterStore());

    // Refs
    const filterSelectorBar: Ref<HTMLElement | null> = ref(null);
    const selectedAction: Ref<FilterActionTyping> = ref(null);

    // Computed
    const guestLabel: ComputedRef<string | number> = computed(() => guestAmountFilter.value > 0
        ? isMobile.value
            ? guestAmountFilter.value
            : t('widget.filter.amount-guest', { amount: guestAmountFilter.value })
        : t('widget.filter.guests'));

    // Methods
    const toggleAction = (action: FilterActionTyping): void => {
        selectedAction.value = selectedAction.value === action ? null : action;
    };

    // Close any open action when clicking outside the component
    onClickOutside(filterSelectorBar, (): void => {
        selectedAction.value = null;
    });

    const formatDate = (date: string): string => {
        const format = isMobile.value ? ConstantsDateTime.dateFormatStandard : ConstantsDateTime.dateFormatPrettyMinimizedYear;
        return DateTime.dayjsObject(date)
            .format(format);
    };
</script>

<template>
    <div
        ref="filterSelectorBar"
        class="filter-selector-bar"
    >
        <!-- Filter actions -->
        <div
            :class="[
                `filter-selector-bar__actions--${styling}`,
                { 'filter-selector-bar__actions--open': selectedAction !== null },
            ]"
            class="filter-selector-bar__actions"
        >
            <button
                v-if="!hideDate"
                :class="{ 'filter-selector-bar__action--selected': selectedAction === 'date' }"
                class="btn btn--input btn--roundless btn--icon-left filter-selector-bar__action"
                type="button"
                @click="toggleAction('date')"
            >
                <Icon
                    name="dateOutlined"
                    size="medium"
                />
                <span>
                    {{ startDateFilter.length ? formatDate(startDateFilter) : t('widget.filter.date') }}
                </span>
            </button>
            <button
                v-if="!hideTime"
                :class="{ 'filter-selector-bar__action--selected': selectedAction === 'time' }"
                class="btn btn--input btn--roundless btn--icon-left filter-selector-bar__action"
                type="button"
                @click="toggleAction('time')"
            >
                <Icon
                    name="clockOutlined"
                    size="medium"
                />
                <span>
                    {{ startTimeFilter.length ? startTimeFilter : t('widget.filter.time') }}
                </span>
            </button>
            <button
                v-if="!hideGuests"
                :class="{ 'filter-selector-bar__action--selected': selectedAction === 'guests' }"
                class="btn btn--input btn--roundless btn--icon-left filter-selector-bar__action"
                type="button"
                @click="toggleAction('guests')"
            >
                <Icon
                    name="guestOutlined"
                    size="medium"
                />
                <span>
                    {{ guestLabel }}
                </span>
            </button>
        </div>

        <!-- Filter content -->
        <div
            v-if="selectedAction?.length"
            :class="[
                `filter-selector-bar__content--${styling}`,
                detached && [
                    'filter-selector-bar__content--detached',
                    `filter-selector-bar__content--${detachedPosition}`,
                ],
            ]"
            class="filter-selector-bar__content"
        >
            <div
                v-if="selectedAction === 'date'"
                class="filter-selector-bar__content-action"
            >
                <div class="filter-selector-bar__inner">
                    <WidgetCalendar
                        v-model="startDateFilter"
                        :offset-days="2"
                        allow-unselect
                        @change="toggleAction('date')"
                    />
                </div>
            </div>
            <div
                v-if="selectedAction === 'time'"
                class="filter-selector-bar__content-action"
            >
                <WidgetHours
                    v-model="startTimeFilter"
                    :columns="5"
                    allow-unselect
                    @change="toggleAction('time')"
                />
            </div>
            <div
                v-if="selectedAction === 'guests'"
                class="filter-selector-bar__content-action"
            >
                <div class="filter-selector-bar__inner">
                    <WidgetRangeInput
                        v-model="guestAmountFilter"
                        :min="minGuestAmount ?? 0"
                        :max="maxGuestAmount ?? 1000"
                        :step="guestAmountStep ?? 1"
                        secondary
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    .filter-selector-bar {
        position: relative;
        inline-size: 100%;

        &__action {
            flex: 1;
            border-inline-end: 1px solid var(--border-tertiary);
            transform: none;

            &:first-of-type {
                border-radius: var(--border-radius-sm) 0 0 var(--border-radius-sm);
            }

            &:last-of-type {
                border: none;
                border-radius: 0 var(--border-radius-sm) var(--border-radius-sm) 0
            }

            &--selected {
                background: var(--background-primary-highlight);
            }
        }

        &__actions {
            display: flex;
            overflow: hidden;
            align-items: center;
            border-radius: var(--border-radius-sm);
            block-size: var(--size-11);
                min-block-size: var(--size-11);

            @include min-width(md) {
                block-size: var(--size-12);
                min-block-size: var(--size-12);
            }

            @include min-width(lg) {
                block-size: var(--size-13);
                min-block-size: var(--size-13);
            }

            &--open {
                border-end-end-radius: 0;
                border-end-start-radius: 0;

                .filter-selector-bar__action {
                    border-end-end-radius: 0;
                    border-end-start-radius: 0;
                }
            }

            &--primary {
                border: 1px solid var(--border-tertiary);
                background: var(--background-primary);
            }

            &--secondary {
                border: 1px solid var(--border-tertiary);
                background: var(--background-secondary);
            }
        }

        &__content {
            position: absolute;
            z-index: var(--zindex-stack-twice);
            padding: var(--spacing-04);
            border-radius: 0 0 var(--border-radius-sm) var(--border-radius-sm);
            inline-size: 100%;
            margin-block-start: -1px;
            max-block-size: 540px;

            &-action {
                display: flex;
                justify-content: center;
                inline-size: 100%;
            }

            &--primary {
                border: 1px solid var(--border-tertiary);
                background: var(--background-primary);
            }

            &--secondary {
                border: 1px solid var(--border-tertiary);
                background: var(--background-secondary);
            }

            // Detached content keeps a fixed width regardless of the actions bar
            // width, instead of stretching to 100% of it.
            &--detached {
                inline-size: var(--filter-selector-detached-width, 700px);
                max-inline-size: calc(100vw - var(--spacing-05));
            }

            // Position the detached content relative to the actions bar.
            &--left {
                border-radius: 0 var(--border-radius-sm) var(--border-radius-sm) var(--border-radius-sm);
                inset-inline: 0 auto;
            }

            &--right {
                inset-inline: auto 0;
            }

            &--center {
                inset-inline-start: 50%;
                transform: translateX(-50%);
            }
        }

        &__inner {
            display: flex;
            justify-content: center;
            padding: var(--spacing-04);
            border-radius: var(--border-radius-md);
            background: rgb(0 0 0 / 0.02);
            inline-size: 100%;
        }
    }
</style>
